/**
 * SoftStopAuditReport — /#/reports/soft-stop-audit
 *
 * Full RULE-ESC-1 audit trail with every manager action: who, when, the
 * lane, the decision, and the reason. Tenant-scoped via soft_stop_events.
 */
import { useEffect, useMemo, useState } from "react";
import ReportShell, { thStyle, tdStyle, BLACK, SLATE, BORDER, EmptyState, LoadingState } from "./_ReportShell";
import { SoftStopAuditRow, downloadCsv, listSoftStopAudit, toCsv } from "@/lib/reports-saturday";

function actionColor(action: string): string {
  switch (action) {
    case "APPROVE":
    case "ADEQUATE":
      return "#16A34A";
    case "RETURN":
    case "INADEQUATE":
      return "#CA8A04";
    case "ESCALATED":
      return "#DC2626";
    default:
      return "#525258";
  }
}

export default function SoftStopAuditReport() {
  const [rows, setRows] = useState<SoftStopAuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await listSoftStopAudit();
        if (!cancelled) setRows(r);
      } catch (err: any) {
        console.error("[SoftStopAuditReport]", err);
        if (!cancelled) setError(err?.message ?? "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const csv = useMemo(() => {
    if (!rows) return "";
    return toCsv(rows.map((r) => ({
      occurred_at: r.occurred_at,
      account_name: r.account_name ?? "",
      lane_key: r.lane_key,
      cp_number: r.cp_number,
      question_key: r.question_key,
      action: r.action,
      actor_name: r.actor_name ?? "",
      actor_role: r.actor_role ?? "",
      reason: r.reason ?? "",
    })));
  }, [rows]);

  return (
    <ReportShell
      title="SOFT_STOP Audit"
      subtitle="RULE-ESC-1 full audit trail with manager actions and reasons."
      rightLabel="SOFT_STOP Audit"
      onExport={rows && rows.length > 0 ? () => downloadCsv("soft-stop-audit.csv", csv) : undefined}
    >
      {error ? (
        <EmptyState message={`Could not load: ${error}`} />
      ) : !rows ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState message="No audit entries yet. Data activates as managers act on SOFT_STOP events." />
      ) : (
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
            <thead>
              <tr>
                <th style={thStyle}>When</th>
                <th style={thStyle}>Account</th>
                <th style={thStyle}>Lane</th>
                <th style={thStyle}>CP / Q</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Actor</th>
                <th style={thStyle}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.audit_id}>
                  <td style={tdStyle}>{new Date(r.occurred_at).toLocaleString()}</td>
                  <td style={tdStyle}>{r.account_name ?? "—"}</td>
                  <td style={tdStyle}>{r.lane_key}</td>
                  <td style={tdStyle}>CP{r.cp_number} / {r.question_key}</td>
                  <td style={tdStyle}>
                    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: `${actionColor(r.action)}22`, color: actionColor(r.action), fontSize: 11, fontWeight: 700 }}>
                      {r.action}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {r.actor_name ?? "—"}
                    {r.actor_role && <div style={{ fontSize: 11, color: SLATE }}>{r.actor_role}</div>}
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 360 }}>{r.reason ?? <span style={{ color: SLATE }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
}
